import "./campaigns.page.css";

import { useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { Link, Navigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";

import {
  addCharacterToCampaign,
  createCampaignEncounter,
  createCampaignQuest,
  deleteCampaignEncounter,
  deleteCampaignQuest,
  fetchAvailableCampaignCharacters,
  fetchCampaignById,
  removeCharacterFromCampaign,
  updateCampaign as updateCampaignRequest,
} from "../../clients/campaigns.client";
import CenteredContainer from "../../components/layout/CenteredContainer/CenteredContainer";
import EncounterDialog from "./EncounterDialog";
import ParticipantPickerDialog from "./ParticipantPickerDialog";
import QuestDialog from "./QuestDialog";

const defaultQuestForm = {
  assignedCharacterIds: [],
  description: "",
  notes: "",
  status: "new",
  title: "",
};

const defaultEncounterForm = {
  description: "",
  enemyText: "",
  experienceReward: 0,
  notes: "",
  rewardItems: [],
  selectedMonsters: [],
  title: "",
};

const formatCharacterClass = (character, t) => {
  const classEntry = character.characterClass?.[0];
  if (!classEntry?.id) return t("characters.noClass");
  return t(`system.classes.${classEntry.id}`);
};

const mapCampaignCharacterOption = (character) => ({
  characterClass: [],
  characterName: character.characterName ?? "",
  id: character.id ?? "",
  level: "",
  name: character.characterName ?? "",
  race: null,
  species: "",
});

const CampaignPage = () => {
  const { t } = useTranslation();
  const { campaignId } = useParams();
  const tokenValue = useSelector((state) => state.auth.tokenValue);
  const [campaign, setCampaign] = useState(null);
  const [characters, setCharacters] = useState([]);
  const [charactersLoaded, setCharactersLoaded] = useState(false);
  const [campaignLoaded, setCampaignLoaded] = useState(false);
  const [questForm, setQuestForm] = useState(defaultQuestForm);
  const [encounterForm, setEncounterForm] = useState(defaultEncounterForm);
  const [editingCampaign, setEditingCampaign] = useState(false);
  const [campaignForm, setCampaignForm] = useState({
    description: "",
    name: "",
  });
  const [isQuestFormOpen, setIsQuestFormOpen] = useState(false);
  const [isEncounterFormOpen, setIsEncounterFormOpen] = useState(false);
  const [isParticipantDialogOpen, setIsParticipantDialogOpen] = useState(false);

  useEffect(() => {
    let cancelled = false;

    const loadCampaign = async () => {
      setCampaignLoaded(false);
      const response = await fetchCampaignById(campaignId, tokenValue);
      if (cancelled) return;

      setCampaign(response.isError ? null : response.data);
      setCampaignLoaded(true);
    };

    loadCampaign();
    return () => {
      cancelled = true;
    };
  }, [campaignId, tokenValue]);

  useEffect(() => {
    let cancelled = false;

    const loadCharacters = async () => {
      setCharactersLoaded(false);
      const response = await fetchAvailableCampaignCharacters(campaignId, tokenValue);

      if (cancelled) return;
      if (response.isError) {
        setCharacters([]);
        setCharactersLoaded(true);
        return;
      }

      const summaries = response.data?.content ?? [];
      setCharacters(summaries.map(mapCampaignCharacterOption));
      setCharactersLoaded(true);
    };

    loadCharacters();
    return () => {
      cancelled = true;
    };
  }, [campaignId, tokenValue]);

  const availableCharacters = useMemo(
    () =>
      characters.filter(
        (character) => !campaign?.memberCharacterIds.includes(character.id)
      ),
    [campaign?.memberCharacterIds, characters]
  );

  const participantCharacters = useMemo(
    () =>
      (campaign?.memberCharacterIds ?? [])
        .map((characterId) =>
          characters.find((character) => character.id === characterId)
        )
        .filter(Boolean),
    [campaign?.memberCharacterIds, characters]
  );

  const closeQuestDialog = () => {
    setQuestForm(defaultQuestForm);
    setIsQuestFormOpen(false);
  };

  const closeEncounterDialog = () => {
    setEncounterForm(defaultEncounterForm);
    setIsEncounterFormOpen(false);
  };

  const refreshCampaign = async () => {
    const response = await fetchCampaignById(campaignId, tokenValue);
    if (response.isError || !response.data) return;
    setCampaign(response.data);
  };

  if (campaignLoaded && !campaign) {
    return <Navigate replace to="/campaigns" />;
  }

  if (!campaignLoaded || !campaign) {
    return (
      <div className="campaigns-shell">
        <CenteredContainer>
          <main className="campaign-page">
            <section className="campaign-empty-state">
              <div className="campaign-empty-card">
                <p>{t("campaigns.loading")}</p>
              </div>
            </section>
          </main>
        </CenteredContainer>
      </div>
    );
  }

  const handleAddParticipants = async (selectedIds) => {
    const responses = await Promise.all(
      selectedIds.map((characterId) =>
        addCharacterToCampaign(campaign.id, characterId, tokenValue)
      )
    );

    if (responses.every((response) => response.isError)) return;
    await refreshCampaign();
    setIsParticipantDialogOpen(false);
  };

  const handleRemoveParticipant = async (characterId) => {
    const response = await removeCharacterFromCampaign(
      campaign.id,
      characterId,
      tokenValue
    );
    if (response.isError) return;

    setCampaign((current) => ({
      ...current,
      memberCharacterIds: current.memberCharacterIds.filter((id) => id !== characterId),
      quests: current.quests.map((quest) => ({
        ...quest,
        assignedCharacterIds: quest.assignedCharacterIds.filter((id) => id !== characterId),
      })),
    }));
  };

  const handleCampaignUpdate = async (event) => {
    event.preventDefault();
    if (!campaignForm.name.trim()) return;

    const response = await updateCampaignRequest(
      campaign.id,
      {
        description: campaignForm.description,
        name: campaignForm.name,
      },
      tokenValue
    );
    if (response.isError || !response.data) return;

    setCampaign(response.data);
    setEditingCampaign(false);
  };

  const handleQuestSubmit = async (nextQuest) => {
    const response = await createCampaignQuest(campaign.id, nextQuest, tokenValue);
    if (response.isError) return;

    await refreshCampaign();
    setQuestForm(defaultQuestForm);
    setIsQuestFormOpen(false);
  };

  const handleEncounterSubmit = async (nextEncounter) => {
    const response = await createCampaignEncounter(
      campaign.id,
      {
        ...nextEncounter,
        experienceReward: nextEncounter.experienceReward,
        rewardItems: nextEncounter.rewardItems,
        enemyEntries: nextEncounter.selectedMonsters.flatMap((monster) =>
          Array.from({ length: monster.quantity }, (_, index) => ({
            challengeRating: "",
            currentHp: 0,
            id: `${monster.catalogId}-${index}`,
            initiative: null,
            maxHp: 0,
            name: monster.name,
            quantity: 1,
          }))
        ),
      },
      tokenValue
    );
    if (response.isError) return;

    await refreshCampaign();
    setEncounterForm(defaultEncounterForm);
    setIsEncounterFormOpen(false);
  };

  return (
    <div className="campaigns-shell">
      <CenteredContainer>
        <main className="campaign-page">
          <header className="campaign-page-header">
            <div>
              <p>{t("campaigns.detailsEyebrow")}</p>
              <h1>{campaign.name}</h1>
              <span className="campaign-page-description">
                {campaign.description || t("campaigns.noDescription")}
              </span>
            </div>
            <div className="campaign-action-row">
              <Link className="campaign-back-link" to="/campaigns">
                {t("campaigns.back")}
              </Link>
              <button
                className="campaign-secondary-button"
                type="button"
                onClick={() => {
                  setCampaignForm({
                    description: campaign.description,
                    name: campaign.name,
                  });
                  setEditingCampaign((value) => !value);
                }}
              >
                {t("campaigns.edit")}
              </button>
            </div>
          </header>

          {editingCampaign && (
            <section className="campaign-section">
              <form className="campaign-form-grid" onSubmit={handleCampaignUpdate}>
                <label htmlFor="edit-campaign-name">
                  {t("campaigns.form.name")}
                  <input
                    id="edit-campaign-name"
                    name="edit-campaign-name"
                    value={campaignForm.name}
                    onChange={(event) =>
                      setCampaignForm((current) => ({
                        ...current,
                        name: event.target.value,
                      }))
                    }
                  />
                </label>
                <label htmlFor="edit-campaign-description">
                  {t("campaigns.form.description")}
                  <textarea
                    id="edit-campaign-description"
                    name="edit-campaign-description"
                    value={campaignForm.description}
                    onChange={(event) =>
                      setCampaignForm((current) => ({
                        ...current,
                        description: event.target.value,
                      }))
                    }
                  />
                </label>
                <div className="campaign-action-row">
                  <button className="campaign-primary-button" type="submit">
                    {t("common.save")}
                  </button>
                  <button
                    className="campaign-ghost-button"
                    type="button"
                    onClick={() => setEditingCampaign(false)}
                  >
                    {t("common.cancel")}
                  </button>
                </div>
              </form>
            </section>
          )}

          <div className="campaign-page-grid">
            <section className="campaign-section">
              <div className="campaign-section-header">
                <div>
                  <p>{t("campaigns.sections.participantsEyebrow")}</p>
                  <h2>{t("campaigns.sections.participants")}</h2>
                  <span>{t("campaigns.sections.participantsHint")}</span>
                </div>
                <button
                  className="campaign-primary-button"
                  type="button"
                  onClick={() => setIsParticipantDialogOpen(true)}
                >
                  {t("campaigns.addParticipant")}
                </button>
              </div>

              {participantCharacters.length === 0 ? (
                <div className="campaign-empty-state">
                  <p>{t("campaigns.emptyParticipants")}</p>
                </div>
              ) : (
                <div className="campaign-members-grid">
                  {participantCharacters.map((character) => (
                    <article className="campaign-member-card" key={character.id}>
                      <h3>{character.characterName || t("characters.unnamed")}</h3>
                      <p>
                        {character.race?.id
                          ? t(`system.races.${character.race.id}`)
                          : t("characters.noRace")}
                      </p>
                      <p className="campaign-member-meta">
                        {formatCharacterClass(character, t)}
                      </p>
                      <button
                        className="campaign-danger-button"
                        type="button"
                        onClick={() => handleRemoveParticipant(character.id)}
                      >
                        {t("campaigns.removeParticipant")}
                      </button>
                    </article>
                  ))}
                </div>
              )}
            </section>

            <section className="campaign-section">
              <div className="campaign-section-header">
                <div>
                  <p>{t("campaigns.sections.questsEyebrow")}</p>
                  <h2>{t("campaigns.sections.quests")}</h2>
                  <span>{t("campaigns.sections.questsHint")}</span>
                </div>
                <button
                  className="campaign-primary-button"
                  type="button"
                  onClick={() => setIsQuestFormOpen(true)}
                >
                  {t("campaigns.addQuest")}
                </button>
              </div>

              {campaign.quests.length === 0 ? (
                <div className="campaign-empty-state">
                  <p>{t("campaigns.emptyQuests")}</p>
                </div>
              ) : (
                <div className="campaign-list-grid">
                  {campaign.quests.map((quest) => (
                    <article className="campaign-entry" key={quest.id}>
                      <div className="campaign-entry-actions">
                        <h3>{quest.title}</h3>
                        <span className="campaign-status-pill" data-status={quest.status}>
                          {t(`campaigns.questStatuses.${quest.status}`)}
                        </span>
                      </div>
                      <p>{quest.description || t("campaigns.noDescription")}</p>
                      {quest.assignedCharacterIds.length > 0 && (
                        <div className="campaign-chip-row">
                          {quest.assignedCharacterIds.map((characterId) => {
                            const character = participantCharacters.find(
                              (entry) => entry.id === characterId
                            );
                            return (
                              <span className="campaign-chip" key={characterId}>
                                {character?.characterName || t("characters.unnamed")}
                              </span>
                            );
                          })}
                        </div>
                      )}
                      {quest.notes && <p>{quest.notes}</p>}
                      <div className="campaign-entry-actions">
                        <button
                          className="campaign-danger-button"
                          type="button"
                          onClick={async () => {
                            const response = await deleteCampaignQuest(
                              campaign.id,
                              quest.id,
                              tokenValue
                            );
                            if (response.isError) return;
                            setCampaign((current) => ({
                              ...current,
                              quests: current.quests.filter((entry) => entry.id !== quest.id),
                            }));
                          }}
                        >
                          {t("common.delete")}
                        </button>
                      </div>
                    </article>
                  ))}
                </div>
              )}
            </section>

            <section className="campaign-section">
              <div className="campaign-section-header">
                <div>
                  <p>{t("campaigns.sections.encountersEyebrow")}</p>
                  <h2>{t("campaigns.sections.encounters")}</h2>
                  <span>{t("campaigns.sections.encountersHint")}</span>
                </div>
                <button
                  className="campaign-primary-button"
                  type="button"
                  onClick={() => setIsEncounterFormOpen(true)}
                >
                  {t("campaigns.addEncounter")}
                </button>
              </div>

              {campaign.encounters.length === 0 ? (
                <div className="campaign-empty-state">
                  <p>{t("campaigns.emptyEncounters")}</p>
                </div>
              ) : (
                <div className="campaign-list-grid">
                  {campaign.encounters.map((encounter) => (
                    <article className="campaign-entry" key={encounter.id}>
                      <Link className="campaign-entry-link" to={`/campaigns/${campaign.id}/encounters/${encounter.id}`}><h3>{encounter.title}</h3></Link>
                      <p>{encounter.description || t("campaigns.noDescription")}</p>
                      <div className="campaign-chip-row">
                        {encounter.enemyEntries.map((enemy) => (
                          <span className="campaign-chip" key={enemy.id}>
                            {enemy.name} x{enemy.count}
                          </span>
                        ))}
                      </div>
                      {encounter.notes && <p>{encounter.notes}</p>}
                      <div className="campaign-entry-actions">
                        <button
                          className="campaign-danger-button"
                          type="button"
                          onClick={async () => {
                            const response = await deleteCampaignEncounter(
                              campaign.id,
                              encounter.id,
                              tokenValue
                            );
                            if (response.isError) return;
                            setCampaign((current) => ({
                              ...current,
                              encounters: current.encounters.filter(
                                (entry) => entry.id !== encounter.id
                              ),
                            }));
                          }}
                        >
                          {t("common.delete")}
                        </button>
                      </div>
                    </article>
                  ))}
                </div>
              )}
            </section>
          </div>
        </main>
        <ParticipantPickerDialog
          availableCharacters={availableCharacters}
          isLoading={!charactersLoaded}
          isOpen={isParticipantDialogOpen}
          onClose={() => setIsParticipantDialogOpen(false)}
          onSubmit={handleAddParticipants}
        />
        <QuestDialog
          form={questForm}
          isOpen={isQuestFormOpen}
          participants={participantCharacters}
          onChange={setQuestForm}
          onClose={closeQuestDialog}
          onSubmit={handleQuestSubmit}
        />
        <EncounterDialog
          form={encounterForm}
          isOpen={isEncounterFormOpen}
          onChange={setEncounterForm}
          onClose={closeEncounterDialog}
          onSubmit={handleEncounterSubmit}
        />
      </CenteredContainer>
    </div>
  );
};

export default CampaignPage;
